//
// messenger.ts
//
// Copyright 2025-Present Couchbase, Inc.
//
// Use of this software is governed by the Business Source License included
// in the file licenses/BSL-Couchbase.txt.  As of the Change Date specified
// in that file, in accordance with the Business Source License, use of this
// software will be governed by the Apache License, Version 2.0, included in
// the file licenses/APL2.txt.
//

import { assert, assertDefined } from "@/util/assert";
import type * as logtape from "@logtape/logtape";
import { pseudoRandomInteger } from "./uuid";


/** The data of a message that sends a request. */
interface Request {
    readonly id: number | null,     // A unique number, or null if no reply is desired
    readonly command: string,       // Identifies the method to call
    readonly args: unknown[],       // Type-erased argument list
}

/** The data of a message that contains a response. */
interface Response {
    readonly re: number,        // The `id` of the initial Request
    readonly result?: unknown,  // Result
    readonly error?: Error,     // Set if the request failed
}

interface Meta {
    readonly meta: string,
    readonly arg?: unknown;
}


// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyAsyncFunction = (...args: any[]) => Promise<any>;

// Evaluates to `Promise<T>`, unless `T` is already a `Promise`, in which case it's just `T`.
type WithPromise<T> = T extends Promise<unknown> ? T : Promise<T>;

type ProtocolLike<S> = { readonly [K in keyof S]: AnyFunction }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NoReplyProtocolLike<S> = { readonly [K in keyof S]: (...args: any[]) => void }


/** The variant of a protocol that you implement: the type of `Messenger.handlers`.
 *  A protocol based on S except that all return types may also be Promises. */
export type ProtocolImpl<S extends ProtocolLike<S>> = {
    readonly [K in keyof S]: (...args: Parameters<S[K]>) => ReturnType<S[K]> | WithPromise<ReturnType<S[K]>>
}

/** The variant of a protocol that you call; the type of `Messenger.call`.
 *  A protocol based on S except that all return types are Promises. */
export type AsyncProtocol<S extends ProtocolLike<S>> = {
    readonly [K in keyof S]: (...args: Parameters<S[K]>) => WithPromise<ReturnType<S[K]>>
}

/** The variant of a protocol for "noreply" calls; the type of `Messenger.noreply`.
 *  A protocol based on S except that all return types are `void`. */
export type NoReplyProtocol<S extends ProtocolLike<S>> = {
    readonly [K in keyof S]: (...args: Parameters<S[K]>) => void
}


// Interface matching MessagePort, BroadcastChannel, WorkerGlobalScope
interface Portlike {
    start?: () => void;
    close?: () => void;
    onmessage: ((message: MessageEvent) => void) | null;
    postMessage(message: unknown): void;
}


const enum PortState {
    Unopened,
    Open,
    Closing,
    Closed
}


/** Error returned from an async request when the Messenger on either side has been closed. */
export class PortClosedError extends Error {
    constructor(message: string) {super(message); this.name = "PortClosed";}
}

/** Error returned from an async request when the other side has no handler for it. */
export class UnhandledRequestError extends Error {
    constructor(message: string) {super(message); this.name = "UnhandledRequest";}
}


export type Refable = object;

type Owner = number & {__brand: 'Owner'};
type RefID = number & {__brand: 'RefID'};

/** A reference to an object owned by the implementor of a Protocol. */
export interface Ref<_T extends Refable> {
    readonly $refOwner: Owner;
    readonly $refID: RefID;
}

function isRef<T extends Refable>(v: unknown): v is Ref<T> {
    return typeof v === 'object' && v !== null && '$refID' in v;
}


/** A simple RPC mechanism for use with Workers, Shared Workers, MessagePorts, BroadcastChannels.
 *  It allows remote APIs to be declared as regular TypeScript interfaces. Handlers can be
 *  implemented as a regular object, and calls can be made via that interface, with async
 *  semantics (return types are converted to Promises.)
 *
 *  The type parameter `S` is an interface declaring the RPC methods that can be _sent_.
 *  The `call` and `noreply` properties expose objects on which you can call these methods.
 *
 *  The type parameter `R` declares the RPC methods that can be _received_.
 *  You must set the `handlers` property to an object matching this interface, which
 *  will be called when a message arrives.
 *
 *  Obviously a Messenger<S,R> communicates with a Messenger<R,S>.
 *
 *  If no methods will be sent in one direction, use `object` to represent the empty interface type.
 *
 *  Don't use `Promise` in the return types; that will be added for you.
 *
 *  Function parameters and return types can be any types supported by JavaScript's
 *  {@ref structuredClone} function. You should probably not pass custom class instances
 *  or objects with prototypes, since they will be stripped into POJOs.
 *
 *  You can also use the special type Ref<T> in the API, as an opaque reference to an object
 *  of type T that's owned by the API's implementor (a handler method.)
 *  - A handler method can return a reference by calling {@ref createRef}, passing in the real `T`
 *    object and returning the `Ref<T>`.
 *  - A subsequent handler call can take a `Ref<T>` as a parameter, and resolve it back to the
 *    original object by calling {@ref resolveRef}.
 *  - When a reference is no longer needed, call {@ref forgetRef}. This makes that `Ref` invalid
 *    and allows the real object to be garbage-collected. (Any further call to {@link resolveRef}
 *    will throw an exception.)
 */
export class Messenger<S extends ProtocolLike<S>, R extends ProtocolLike<R>> {

    /** Creates a Web Worker and returns a Messenger that sends to it. */
    static withNewWorker<S extends ProtocolLike<S>, R extends ProtocolLike<R> = object>(
        script: URL,
        name?: string): Messenger<S, R>
    {
        const m = new Messenger<S, R>(new Worker(script, {type: 'module', name}), name ?? "Boss");
        m.handlers = {} as R;
        return m;
    }


    /** From a Worker script, connects to its creator. */
    static inWorker<S extends ProtocolLike<S>, R extends ProtocolLike<R>>(
        name?: string): Messenger<S, R>
    {
        // In a Worker, the global context (`self`) matches the Portlike API.
        return new Messenger<S, R>(self, name ?? self.name ?? "Worker");
    }


    /** Creates a Messenger for communicating over an existing MessagePort. */
    static withPort<S extends ProtocolLike<S>, R extends ProtocolLike<R>>(
        port: MessagePort,
        name?: string): Messenger<S, R>
    {
        return new Messenger<S, R>(port, name ?? "Port");
    }


    /** Creates a MessageChannel and returns a pair of Messengers that communicate over
     *  its MessagePorts. */
    static withChannel<S extends ProtocolLike<S>, R extends ProtocolLike<R>>(
        name1?: string, name2?: string): [Messenger<S,R>, Messenger<R,S>]
    {
        const chan = new MessageChannel();
        return [ Messenger.withPort<S,R>(chan.port1, name1),
                 Messenger.withPort<R,S>(chan.port2, name2 ?? name1) ];
    }


    /** Connects to a SharedWorker that runs the script at the given URL, with the given name.
     *  If a SharedWorker with the same URL and name is running, creates a new connection to it.
     *  Otherwise it creates a new SharedWorker and connects.
     *
     *  Within the SharedWorker script, use {@link SharedWorkerManager} to handle connections.
     *  @parameter script  The URL of the worker's script. It's best to create this relative to
     *                     `import.meta.url`, e.g.: `new URL("worker.js", import.meta.url)`
     *  @parameter name  A string identifying a particular SharedWorker instance. Can be used to
     *                   create multiple independent workers running the same code.*/
    static withSharedWorker<S extends ProtocolLike<S>, R extends ProtocolLike<R> = object>(
        script: URL,
        options?: WorkerOptions): Messenger<S, R>
    {
        const worker = new SharedWorker(script, options);
        const m = new Messenger<S, R>(worker.port, options?.name ?? "Shared");
        m.setSharedWorker(worker);
        m.handlers = {} as R;
        return m;
    }


    /** Creates a BroadcastChannel with the given name. When a message is sent by an instance,
     *  it will be received by all other instances with the same name, in this context and any
     *  other contexts with the same origin.
     *  The same protocol is used for sending and receiving.
     *  Replies are not possible; all return types must be `void`, and you must send messages
     *  `noreply`. */
    static withBroadcastChannel<S extends NoReplyProtocolLike<S>>(
        channelName: string): Messenger<S,S>
    {
        return new Messenger<S, S>(new BroadcastChannel(channelName), channelName, true);
    }


    private constructor(private readonly port: Portlike,
                        readonly name?: string,
                        private readonly isBroadcast = false)
    {
        // Listen for the port closing:
        if (port instanceof MessagePort || (typeof Worker !== 'undefined' && port instanceof Worker)) {
            port.addEventListener('close', event => {
                this.handlePortClosed(event instanceof CloseEvent ? event.reason : `closed from other side`);
            });
        }

        // Instantiate `this.call` as a proxy object whose methods send RPC calls:
        const c: Record<string, AnyAsyncFunction> = {};
        this.call = new Proxy(c, {
            get: (callable, cmd, _receiver) => {
                if (typeof cmd !== 'string')
                    return undefined;
                let value = callable[cmd];
                if (!value) {
                    value = async (...args) => {
                        return this.call_(cmd, args);
                    };
                    callable[cmd] = value;
                }
                return value;
            }
        }) as AsyncProtocol<S>;

        // Instantiate `this.noreply` as a proxy object whose methods send RPC calls:
        const nr: Record<string, (...args: unknown[])=>void> = {};
        this.noreply = new Proxy(nr, {
            get: (callable, cmd, _receiver) => {
                if (typeof cmd !== 'string')
                    return undefined;
                let value = callable[cmd];
                if (!value) {
                    value = (...args: unknown[]) => {
                        this.callNoReply_(cmd, args);
                    };
                    callable[cmd] = value;
                }
                return value;
            }
        }) as NoReplyProtocol<S>;
    }


    private setSharedWorker(worker: SharedWorker) {
        worker.addEventListener('error', event => {
            const message = `SharedWorker failed: ${event.message} (at ${event.filename} line ${event.lineno})`;
            this.logger?.error(message);
            this.handlePortClosed(message);
        });
    }


    /** Set a LogTape logger here to enable logging. */
    logger?: logtape.Logger;


    /** An object defining the implementations of the received functions (`R`) */
    handlers!: ProtocolImpl<R>;


    toString(): string {
        return `Messenger[${this.name}]`;
    }


    /** Opens the connection to the other side and begins receiving requests. */
    open() {
        switch (this.#state) {
            case PortState.Unopened:
                assert(this.handlers !== undefined, "No handlers defined");
                this.logger?.debug(`${this} opening!`);
                this.port.onmessage = this.handleMessage.bind(this);
                this.port.start?.();
                this.#state = PortState.Open;
                break;
            case PortState.Open:
                break;
            default:
                throw Error("Messenger has already been opened");
        }
    }


    /** True if the Messenger has been opened but not yet closed. */
    get isOpen(): boolean {return this.#state === PortState.Open;}


    /** An object that implements the sending schema, with all return types wrapped in Promises.
     *  Calling methods of this object sends an async RPC call to the other side. */
    readonly call: AsyncProtocol<S>;

    /** An object that implements the sending schema, except all return types are `void`.
     *  Requests sent this way are tagged `noreply`, and the other side won't send a reply.
     *  (Any reply returned by the other side's handler is discarded without being sent.) */
    readonly noreply: NoReplyProtocol<S>;


    //-------- Refs:


    /** Wraps an arbitrary object in a `Ref` that can be returned from a call.
     *  (Multiple calls to this with the same object are fine; they return an equivalent Ref.) */
    createRef<T extends Refable>(value: T): Ref<T> {
        if (isRef<T>(value)) {
            this.assertMyRef(value);
            return value;
        }
        let id = this.#localVals.get(value);
        if (id === undefined) {
            id = ++this.#lastRef as RefID;
            this.#localRefs.set(id, value);
            this.#localVals.set(value, id);
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            this.logger?.trace(`${this} created Ref.${id} for ${value}`);
        }
        return {$refID: id, $refOwner: this.#owner};
    }


    /** Given a Ref created earlier by `createRef`, returns the object it wraps. */
    resolveRef<T extends Refable>(ref: Ref<T>): T {
        this.assertMyRef(ref);
        const value = this.#localRefs.get(ref.$refID);
        if (value === undefined)
            throw ReferenceError(`Resolving unknown/deleted Ref with id ${ref.$refID}`);
        return value as T;
    }


    /** Returns true if a Ref created by me is still valid (not forgotten.) */
    isValidLocalRef<T extends Refable>(ref: Ref<T>): boolean {
        assert(isRef(ref), "Not a Ref");
        return ref.$refOwner === this.#owner && this.#localRefs.has(ref.$refID);
    }


    /** Destroys a Ref, making the source object garbage-collectable. You can pass:
     *  - A local object that you're previously called createRef on
     *  - A Ref referring to a local object
     *  - A Ref received from the peer referring to a remote object (will be forwarded to the peer.)
     */
    forgetRef<T extends Refable>(valueOrRef: Ref<T> | T): void {
        let id: RefID | undefined;
        let value: object | undefined;
        if (isRef<T>(valueOrRef)) {
            if (valueOrRef.$refOwner === this.#owner) {
                // It's a Ref I created:
                id = valueOrRef.$refID;
                value = this.#localRefs.get(id);
                if (value === undefined)
                    this.logger?.warn(`${this}: double deletion of local Ref.${id}`);
            } else {
                // It's a Ref the peer (presumably) created:
                this.logger?.trace(`${this} deleting remote Ref.${valueOrRef.$refID}`);
                this.postMessage({meta: 'forgetRef', arg: valueOrRef});
            }
        } else {
            // It's a value:
            value = valueOrRef;
            id = this.#localVals.get(valueOrRef);
            if (id === undefined) {
                this.logger?.warn(`${this}: deleting Ref for unknown object ${JSON.stringify(valueOrRef)}`);
            }
        }
        if (id !== undefined) {
            this.#localRefs.delete(id);
            this.logger?.trace(`${this} deleted local Ref.${id}`);
        }
        if (value !== undefined)
            this.#localVals.delete(value);
    }


    private assertMyRef<T extends Refable>(ref: Ref<T>) {
        if (!isRef<T>(ref))
            throw ReferenceError(`Not a Ref: ${JSON.stringify(ref)}`);
        if (ref.$refOwner !== this.#owner)
            throw ReferenceError(`Not a local Ref (owner is ${ref.$refOwner}, not ${this.#owner})`);
        if (ref.$refID > this.#lastRef)
            throw ReferenceError(`Invalid local Ref id ${ref.$refID} (max is ${this.#lastRef}`);
    }


    //-------- Port I/O:


    /** Implementation of sending a request.  */
    private async call_(command: string, args: unknown[]): Promise<unknown> {
        if (!this.isOpen)
            throw new PortClosedError("Messenger is not open");
        if (this.isBroadcast)
            throw new Error("broadcast Messenger cannot receive replies");
        const id = ++this.#lastReqID;
        this.logger?.info(`${this} sending '${command}' (#${id})`);
        const pwr = Promise.withResolvers<unknown>();
        this.#pending.set(id, pwr);
        this.postMessage({id, command, args});
        return pwr.promise;
    }


    /** Implementation of sending a noreply request. */
    private callNoReply_(command: string, args: unknown[]) {
        switch (this.#state) {
            case PortState.Unopened:
                throw new PortClosedError("Messenger is not open yet");
            case PortState.Open:
                this.logger?.info(`${this} sending noreply '${command}'`);
                this.postMessage({id: null, command, args});
                break;
            case PortState.Closing:
            case PortState.Closed:
                this.logger?.warn(`${this} ignoring noreply '${command}' after port closed`);
                break;
        }
    }


    /** Sends a message through the port. */
    private postMessage(r: Request | Response | Meta) {
        if (this.#state < PortState.Closed)
            this.port.postMessage(r);
    }


    /** Handles a message from the port. */
    private handleMessage(msg: MessageEvent) {
        const r = msg.data as Request | Response | Meta;
        if ('id' in r)
            this.handleRequest(r);
        else if ('re' in r)
            this.handleResponse(r);
        else {
            switch (r.meta) {
                case 'closing':
                    this.handlePeerClosing();
                    break;
                case 'forgetRef': {
                    const ref = r.arg as Ref<object>;
                    this.assertMyRef(ref);
                    this.forgetRef(ref);
                    break;
                }
                default:
                    this.logger?.warn(`${this} Received unknown meta message "${r.meta}"`);
                    break;
            }
        }
    }


    /** Handles an incoming Request. */
    private handleRequest(r: Request) {
        this.logger?.debug(`${this} received message "${r.command}"`);
        const id = r.id;
        const handler = (this.handlers as Record<string,(...args:unknown[])=>unknown>)[r.command];
        if (handler) {
            // Call the handler:
            let result;
            try {
                result = handler(...r.args);
            } catch (x) {
                this.logger?.error(`${this} caught exception handling "${r.command}": ${x}`);
                if (id !== null)
                    this.postMessage({re: id, error: x as Error});
                return;
            }

            if (id === null) {
                // Noreply mode:
                if (result !== undefined && result !== null)
                    this.logger?.warn(`${this} ignoring result of noreply "${r.command}"`);
            } else {
                this.sendResponse(result, id);
            }

        } else {
            // Unknown command:
            const message = `${this} has no handler for "${r.command}"`;
            if (id !== null)
                this.postMessage({re: id, result: {}, error: new UnhandledRequestError(message)});
            else
                this.logger?.error(message);
        }
    }


    /** Delivers a response to the peer. */
    private sendResponse(result: unknown, id: number) {
        if (result instanceof Promise) {
            // Result is a promise:
            ++this.#sendingResponses;
            result.then(
                response => {
                    this.postMessage({re: id, result: response});
                },
                error => {
                    this.postMessage({re: id, error: error as Error});
                }
            ).finally( () => {
                if (--this.#sendingResponses === 0)
                    this.maybeDisconnect();
            });
        } else {
            // Result is immediate:
            this.postMessage({re: id, result: result});
        }
    }


    /** Handles an incoming Response. */
    private handleResponse(r: Response) {
        this.logger?.debug(`${this} received response to #${r.re}`);
        const pwr = this.#pending.get(r.re);
        assertDefined(pwr, `Was not expecting a response to #${r.re}`);
        this.#pending.delete(r.re);
        if (r.error)
            pwr.reject(r.error);
        else
            pwr.resolve(r.result);
    }


    //-------- Closing:


    /** Adds a handler that will be called when this Messenger initiates closing (from either side.) */
    onClose(handler: (messenger: Messenger<S, R>)=>void) {
        this.#onClose.push(handler);
    }


    /** Adds a handler that will be called when this Messenger's port disconnects. */
    onDisconnect(handler: (messenger: Messenger<S, R>)=>void) {
        this.#onDisconnect.push(handler);
    }


    /** Initiates an orderly close of both sides. After this you may not send any new requests,
     *  but you can still receive responses to earlier requests, and your async request handlers
     *  may finish sending responses. After all that, the Messengers will both disconnect. */
    close() {
        if (this.#state === PortState.Open) {
            if (this.isBroadcast) {
                this.closing();
                this.#selfClosing = this.#peerClosing = true;
                this.disconnect();
            } else {
                this.logger?.trace(`${this} Requesting close`);
                this.#selfClosing = true;
                this.postMessage({meta: 'closing'});
                this.closing();
            }
        }
    }


    /** Changes state to Closing and calls onClose functions. */
    private closing() {
        if (this.#state === PortState.Open) {
            this.#state = PortState.Closing;
            for (const onClose of this.#onClose)
                onClose(this);
        }
    }


    /** Handles a 'closing' meta-message from the peer. */
    private handlePeerClosing() {
        if (!this.#peerClosing) {
            this.logger?.trace(`${this} Peer requests close`);
            this.#peerClosing = true;
            if (!this.#selfClosing)
                this.close();
            this.maybeDisconnect();
        }
    }


    /** Returns true if it's finally OK to close the port. */
    private okToDisconnect(): boolean {
        return this.#selfClosing && this.#peerClosing &&
               this.#pending.size === 0 && this.#sendingResponses === 0;
    }


    private maybeDisconnect() {
        if (this.okToDisconnect())
            this.disconnect();
    }


    /** Abruptly disconnects the port. Use {@ref close} instead for an orderly shutdown. */
    disconnect() {
        if (this.#state < PortState.Closed) {
            this.logger?.debug(`${this} Closing port`);
            this.close();
            this.port.onmessage = null;
            this.port.close?.();
            this.handlePortClosed(`closed from this side`);
            for (const onDisconnect of this.#onDisconnect)
                onDisconnect(this);
        }
    }


    /** Called when the port sends a 'close' event. */
    private handlePortClosed(reason: string) {
        if (this.okToDisconnect())
            this.logger?.debug(`${this} ${reason}`);
        else
            this.logger?.warn(`${this} Unexpected closure: ${reason}`);
        this.#state = PortState.Closed;
        // Respond with an error to any requests that haven't received a response yet:
        const error = new PortClosedError(reason);
        for (const pwr of this.#pending.values())
            pwr.reject(error);
        this.#pending.clear();
    }


    #state               = PortState.Unopened;      // Connection state
    #selfClosing         = false;                   // True if I sent a 'closing' message
    #peerClosing         = false;                   // True if I received a 'closing' message
    readonly #pending    = new Map<number,PromiseWithResolvers<unknown>>; // Promises for responses
    #lastReqID           = 0;                                       // Last request 'id' I sent
    #sendingResponses    = 0;                                       // # responses I owe the peer

    readonly #onClose    = new Array<(m: Messenger<S, R>)=>void>;  // To call on 'close'
    readonly #onDisconnect = new Array<(m: Messenger<S, R>)=>void>;// To call on disconnect

    readonly #owner      = pseudoRandomInteger(1 << 30) as Owner;   // Random owner ID for Refs
    readonly #localRefs  = new Map<RefID, object>;                  // Local Refs by ID
    readonly #localVals  = new Map<object, RefID>;                  // Local Refs by value
    #lastRef             = 0 as RefID;                              // Last RefID I generated
}



export interface SharedWorkerManagerDelegate<S extends ProtocolLike<S>, R extends ProtocolLike<R>> {
    /** A client has connected to this shared worker.
     *  You must initialize the new Messenger's handlers. */
    onConnect(messenger: Messenger<S, R>): void;
    /** A client is closing a connection. */
    onClose?(messenger: Messenger<S, R>): void;
    /** A connection has disconnected. */
    onDisconnect?(messenger: Messenger<S, R>): void;
}


/** Within a SharedWorker, waits for connections and handles requests from them. */
export class SharedWorkerManager<R extends ProtocolLike<R>, S extends ProtocolLike<S> = object> {

    constructor(readonly delegate: SharedWorkerManagerDelegate<S, R>,
                readonly name?: string) { }

    /** Begins listening for connections. */
    open() {
        if (typeof addEventListener !== 'undefined') {
            assert(!this.#connectListener, "already open");
            this.#connectListener = this.handleConnection.bind(this);
            addEventListener('connect', this.#connectListener);
        }
    }

    /** Stops listening for connections, and closes all open connections. */
    close() {
        if (this.#connectListener) {
            removeEventListener('connect', this.#connectListener);
            this.#connectListener = undefined;
        }
        for (const conn of this.#connections)
            conn.close();
    }

    /** An array of the Messengers of all open connections. */
    get connections(): Messenger<S,R>[] {
        return Array.from(this.#connections);
    }

    private handleConnection(ev: Event) {
        const event = ev as MessageEvent;
        const port = event.ports[0];
        const messenger = Messenger.withPort<S, R>(port, `${this.name}.${event.origin}`);
        this.connectMessenger(messenger);
        messenger.open();
    }

    /** Explicitly connects a Messenger.
     *  Can be called directly in "fake" mode, i.e. when not running in a real SharedWorker. */
    connectMessenger(messenger: Messenger<S, R>) {
        this.delegate.onConnect(messenger);
        this.#connections.add(messenger);
        messenger.onClose( _m => {
            this.delegate.onClose?.(messenger);
        });
        messenger.onDisconnect( _m => {
            this.#connections.delete(messenger);
            this.delegate.onDisconnect?.(messenger);
        });
        messenger.open();
    }

    #connectListener?       : (event: Event)=>void;
    readonly #connections   = new Set<Messenger<S, R>>();
}
